import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import { Download } from "lucide-react";
import collingsLogo from "../../assets/logo_collings-512.png";

/**
 * Branded, framed "Scan Me" QR code for the public property share link.
 *
 * `qr-code-styling` only renders the bare code, so we generate the styled QR
 * (rounded/dotted modules, Collings logo in the centre) off-screen, then COMPOSE
 * it onto our own canvas with a rounded border frame and a "Scan Me" pill at the
 * bottom. The composed canvas is what we both preview and download, so the saved
 * PNG matches the on-screen graphic exactly.
 *
 * `errorCorrectionLevel: "H"` is used deliberately — the centre logo removes
 * modules, and the high redundancy keeps the code scannable.
 */

const DOTS = "#2BA9C4"; // Collings teal — matches the brand, dark enough to scan
const EYES = "#0F2A43"; // navy finder patterns for contrast / reliable scanning
const FRAME = "#0F2A43";

// Style presets shown in the picker. Each maps to qr-code-styling module +
// corner shapes. Colours stay constant so the brand reads consistently.
const STYLES = {
  rounded: { label: "Rounded", dotsType: "rounded", cornerSquare: "extra-rounded", cornerDot: "dot" },
  dots: { label: "Dots", dotsType: "dots", cornerSquare: "dot", cornerDot: "dot" },
  classy: { label: "Classy", dotsType: "classy-rounded", cornerSquare: "extra-rounded", cornerDot: "square" },
  classic: { label: "Classic", dotsType: "square", cornerSquare: "square", cornerDot: "square" },
};

const LOGO_SRC = collingsLogo;
const QR_PX = 900; // off-screen render resolution for a crisp downloaded PNG

function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Draw the supplied QR image inside a rounded frame with a "Scan Me" badge and
 * return the result as a PNG data URL.
 */
function composeFramedQr(qrImg) {
  const W = QR_PX;
  const border = W * 0.045;
  const radius = W * 0.1;
  const badgeH = W * 0.135;
  const H = W + badgeH * 0.6; // room for the badge overlapping the bottom edge

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // White rounded panel + thick frame.
  const fx = border / 2;
  const fy = border / 2;
  const fw = W - border;
  const fh = W - border;
  roundRectPath(ctx, fx, fy, fw, fh, radius);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.lineWidth = border;
  ctx.strokeStyle = FRAME;
  ctx.stroke();

  // QR inside the frame.
  const inset = border + W * 0.04;
  const qrSize = W - inset * 2;
  ctx.drawImage(qrImg, inset, inset, qrSize, qrSize);

  // "Scan Me" pill, centred on the bottom border.
  const bw = W * 0.46;
  const bh = badgeH;
  const bx = (W - bw) / 2;
  const by = fy + fh - bh / 2;
  roundRectPath(ctx, bx, by, bw, bh, bh / 2);
  ctx.fillStyle = "#000000";
  ctx.fill();

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffffff";
  // Phone glyph on the left, label to its right.
  ctx.font = `${bh * 0.5}px Arial`;
  ctx.fillText("📱", bx + bh * 0.6, by + bh / 2);
  ctx.font = `bold ${bh * 0.4}px Arial`;
  ctx.fillText("Scan Me", W / 2 + bh * 0.3, by + bh / 2 + bh * 0.02);

  return canvas.toDataURL("image/png");
}

function blobToImage(blob) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

export default function StyledShareQr({ url, filename = "property" }) {
  const [styleKey, setStyleKey] = useState("rounded");
  const [dataUrl, setDataUrl] = useState(null);
  const qrRef = useRef(null);

  const options = useMemo(() => {
    const s = STYLES[styleKey];
    return {
      width: QR_PX,
      height: QR_PX,
      type: "canvas",
      data: url,
      image: LOGO_SRC,
      margin: 0,
      qrOptions: { errorCorrectionLevel: "H" },
      dotsOptions: { color: DOTS, type: s.dotsType },
      backgroundOptions: { color: "#ffffff" },
      cornersSquareOptions: { color: EYES, type: s.cornerSquare },
      cornersDotOptions: { color: DOTS, type: s.cornerDot },
      imageOptions: { crossOrigin: "anonymous", margin: 6, imageSize: 0.32 },
    };
  }, [url, styleKey]);

  // Render the QR off-screen, then compose it into the framed graphic.
  useEffect(() => {
    let cancelled = false;
    if (!qrRef.current) qrRef.current = new QRCodeStyling(options);
    else qrRef.current.update(options);

    qrRef.current
      .getRawData("png")
      .then((blob) => (blob ? blobToImage(blob) : null))
      .then((img) => {
        if (!cancelled && img) setDataUrl(composeFramedQr(img));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [options]);

  const download = () => {
    if (!dataUrl) return;
    const link = document.createElement("a");
    link.download = `share-qr-${filename}.png`;
    link.href = dataUrl;
    link.click();
  };

  return (
    <div className="flex flex-col items-center">
      <div className="w-[220px]">
        {dataUrl ? (
          <img src={dataUrl} alt="Property share QR code" className="w-full h-auto" />
        ) : (
          <div className="aspect-square w-full bg-white border rounded-lg animate-pulse" />
        )}
      </div>

      <div className="mt-3 flex flex-wrap justify-center gap-1.5">
        {Object.entries(STYLES).map(([key, s]) => (
          <button
            key={key}
            type="button"
            onClick={() => setStyleKey(key)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all ${styleKey === key
              ? "bg-[#55C5D0] text-white border-[#55C5D0] shadow-sm"
              : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-gray-500 mt-3 text-center max-w-xs">
        Scan to open the public listing, or download to drop into print or social ads.
      </p>

      <button
        type="button"
        onClick={download}
        disabled={!dataUrl}
        className="mt-3 px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#55C5D0] hover:bg-[#4AADB7] text-white shadow-sm flex items-center gap-1.5 disabled:opacity-50"
      >
        <Download className="w-3.5 h-3.5" />
        Download PNG
      </button>
    </div>
  );
}
